import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  serviceCategoryLabels,
  serviceCategoryOptions,
  uploadRfqImage,
  urgencyLabels,
  urgencyOptions,
  formatQuotePrice,
  useAnswerQuestion,
  useCancelRfq,
  useDeleteRfqImage,
  useRfq,
  useRfqImageUrl,
  useRfqQuotes,
  useRfqThreads,
  useSelectQuote,
  useSendThreadMessage,
  useUpdateRfq,
  type JobProjection,
  type QuoteSelection,
  type Rfq,
  type RfqImage,
  type ServiceCategory,
  type Urgency,
} from "../rfqs/api.js";
import { detectUploadMime, MAX_UPLOAD_BYTES } from "../util/imageCompression.js";
import { BinderMediaThumb } from "../properties/BinderMediaThumb.js";
import { entryTypeLabels } from "../properties/binder-templates.js";
import type { BinderEntryType } from "../properties/binder-api.js";
import { Button } from "../ui/Button.js";
import { SectionLabel } from "../ui/Card.js";
import { CameraIcon, CheckIcon, ChevronLeftIcon, XMarkIcon } from "../ui/icons.js";
import { Skeleton } from "../ui/Skeleton.js";
import { StatusBadge } from "../ui/StatusBadge.js";

export function RfqDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: rfq, isLoading, error } = useRfq(id);
  const updateMutation = useUpdateRfq(id ?? "");
  const cancelMutation = useCancelRfq();

  const [editing, setEditing] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [serviceCategory, setServiceCategory] = useState<ServiceCategory>("plumbing");
  const [urgency, setUrgency] = useState<Urgency>("normal");
  const [description, setDescription] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  if (!id) return <Navigate to="/app/rfqs" replace />;
  if (isLoading) return <DetailSkeleton />;
  if (error || !rfq) {
    return (
      <div className="mx-auto max-w-md p-4">
        <p className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
          Request not found.
        </p>
        <Link
          to="/app/rfqs"
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-700"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back to requests
        </Link>
      </div>
    );
  }

  const isEditable = rfq.status === "SUBMITTED";

  function startEditing() {
    if (!rfq) return;
    setServiceCategory(rfq.serviceCategory);
    setUrgency(rfq.urgency);
    setDescription(rfq.description);
    setEditError(null);
    setEditing(true);
  }

  async function saveEdit() {
    setEditError(null);
    try {
      await updateMutation.mutateAsync({ serviceCategory, urgency, description });
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Couldn't save changes");
    }
  }

  return (
    <div className="mx-auto max-w-md">
      <header className="border-b border-slate-200 bg-white px-4 pb-4 pt-4">
        <Link
          to="/app/rfqs"
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Requests
        </Link>
        <div className="mt-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-bold tracking-tight text-slate-900">
              {serviceCategoryLabels[rfq.serviceCategory]}
            </h1>
            <p className="mt-1 text-sm text-slate-600">{urgencyLabels[rfq.urgency]}</p>
          </div>
          <StatusBadge status={rfq.status} />
        </div>
      </header>

      {!editing ? (
        <div className="space-y-7 px-4 py-6">
          <Section label="Property">
            <p className="text-base text-slate-900">
              {rfq.location.street}
              {rfq.location.unit ? `, ${rfq.location.unit}` : ""}
            </p>
            <p className="text-sm text-slate-600">
              {rfq.location.city}, {rfq.location.state} {rfq.location.postalCode}
            </p>
          </Section>

          <Section label="Description">
            <p className="whitespace-pre-wrap text-[15px] font-medium leading-relaxed text-slate-900">
              {rfq.description}
            </p>
          </Section>

          <Section label="Status">
            <StatusTimeline rfq={rfq} />
          </Section>

          <Section label="Photos">
            <ImageGallery rfqId={rfq.id} images={rfq.images ?? []} canEdit={isEditable} />
          </Section>

          {(rfq.attachedBinder?.length ?? 0) > 0 && (
            <Section label="Property file">
              <AttachedBinderList rfq={rfq} />
            </Section>
          )}

          <QuotesSection rfqId={rfq.id} rfqOpen={isEditable} />

          <MessagesSection rfqId={rfq.id} />

          {(rfq.questions?.length ?? 0) > 0 && (
            <Section label="Contractor questions">
              <QuestionsList rfq={rfq} canAnswer={isEditable} />
            </Section>
          )}

          {isEditable && (
            <div className="flex gap-3 pt-2">
              <Button variant="secondary" size="lg" fullWidth onClick={startEditing}>
                Edit
              </Button>
              <Button
                variant="destructive"
                size="lg"
                fullWidth
                onClick={() => setConfirmingCancel(true)}
              >
                Cancel request
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4 p-4">
          {editError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
              {editError}
            </div>
          )}
          <FieldLabel label="Type">
            <select
              value={serviceCategory}
              onChange={(e) => setServiceCategory(e.target.value as ServiceCategory)}
              className={fieldClass}
            >
              {serviceCategoryOptions.map((c) => (
                <option key={c} value={c}>
                  {serviceCategoryLabels[c]}
                </option>
              ))}
            </select>
          </FieldLabel>
          <FieldLabel label="Urgency">
            <select
              value={urgency}
              onChange={(e) => setUrgency(e.target.value as Urgency)}
              className={fieldClass}
            >
              {urgencyOptions.map((u) => (
                <option key={u} value={u}>
                  {urgencyLabels[u]}
                </option>
              ))}
            </select>
          </FieldLabel>
          <FieldLabel label="Description">
            <textarea
              rows={6}
              maxLength={2000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="block w-full rounded-xl border border-slate-300 p-3 text-base focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600"
            />
          </FieldLabel>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" size="lg" fullWidth onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              size="lg"
              fullWidth
              loading={updateMutation.isPending}
              disabled={description.trim().length === 0}
              onClick={saveEdit}
            >
              {updateMutation.isPending ? "Saving" : "Save"}
            </Button>
          </div>
        </div>
      )}

      {confirmingCancel && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-20 flex items-end bg-slate-900/40 px-4 pb-[max(env(safe-area-inset-bottom),1rem)]"
          onClick={() => setConfirmingCancel(false)}
        >
          <div
            className="w-full rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-slate-900">Cancel this request?</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              You can submit a new request anytime; cancelling stops this one from being worked on.
            </p>
            <div className="mt-5 flex gap-3">
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                onClick={() => setConfirmingCancel(false)}
              >
                Keep it
              </Button>
              <Button
                variant="destructive"
                size="lg"
                fullWidth
                loading={cancelMutation.isPending}
                onClick={async () => {
                  await cancelMutation.mutateAsync(rfq.id);
                  setConfirmingCancel(false);
                }}
              >
                {cancelMutation.isPending ? "Cancelling" : "Cancel request"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const fieldClass =
  "block w-full min-h-[44px] rounded-xl border border-slate-300 px-3 text-base focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600";

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

/**
 * Message threads (Phase 2 unit 14): one private thread per quoting
 * contractor. Threads the contractor started appear too; a thread can be
 * started from any active quote.
 */
function MessagesSection({ rfqId }: { rfqId: string }) {
  const { data: threadData } = useRfqThreads(rfqId);
  const { data: quoteData } = useRfqQuotes(rfqId);
  const sendMessage = useSendThreadMessage(rfqId);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Merge: threads with messages + quote-derived empty threads (so the
  // customer can START a conversation with any active quoter).
  const threads = new Map((threadData?.threads ?? []).map((t) => [t.invitationId, { ...t }]));
  for (const q of quoteData?.quotes ?? []) {
    if (!q.invitationId || q.status !== "ACTIVE" || threads.has(q.invitationId)) continue;
    const selectionOpen =
      !quoteData?.selection ||
      (quoteData.selection.status === "SELECTED" && quoteData.selection.quoteId === q.quoteId);
    threads.set(q.invitationId, {
      invitationId: q.invitationId,
      businessName: q.contractorProfile?.businessName ?? null,
      open: selectionOpen,
      messages: [],
    });
  }
  if (threads.size === 0) return null;

  return (
    <Section label="Messages">
      <div className="space-y-4">
        {[...threads.values()].map((thread) => (
          <div
            key={thread.invitationId}
            className="rounded-2xl border border-slate-200 bg-white p-3"
          >
            <p className="text-sm font-semibold text-slate-900">
              {thread.businessName ?? "A contractor"}
            </p>
            {thread.messages.length > 0 && (
              <div className="mt-2 space-y-2">
                {thread.messages.map((m) => (
                  <div
                    key={m.messageId}
                    className={`max-w-[85%] rounded-2xl p-2.5 text-sm leading-relaxed ${
                      m.sender === "customer"
                        ? "ml-auto bg-brand-700 text-white"
                        : "bg-slate-100 text-slate-900"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{m.body}</p>
                    <p
                      className={`mt-1 text-[11px] ${
                        m.sender === "customer" ? "text-teal-100" : "text-slate-400"
                      }`}
                    >
                      {new Date(m.sentAt).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {thread.open ? (
              <form
                className="mt-2 flex items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const body = (drafts[thread.invitationId] ?? "").trim();
                  if (body.length === 0) return;
                  sendMessage.mutate(
                    { invitationId: thread.invitationId, body },
                    {
                      onSuccess: () => setDrafts((d) => ({ ...d, [thread.invitationId]: "" })),
                    },
                  );
                }}
              >
                <textarea
                  className="w-full rounded-xl border border-slate-300 p-2.5 text-sm"
                  rows={1}
                  maxLength={2000}
                  placeholder={
                    thread.messages.length === 0 ? "Start a conversation..." : "Reply..."
                  }
                  value={drafts[thread.invitationId] ?? ""}
                  onChange={(e) =>
                    setDrafts((d) => ({ ...d, [thread.invitationId]: e.target.value }))
                  }
                />
                <Button type="submit" size="sm" disabled={sendMessage.isPending}>
                  Send
                </Button>
              </form>
            ) : (
              <p className="mt-2 text-xs text-slate-500">This conversation is closed.</p>
            )}
          </div>
        ))}
        {sendMessage.error && <p className="text-sm text-rose-700">{sendMessage.error.message}</p>}
      </div>
    </Section>
  );
}

/**
 * Quote compare-and-select (Phase 2 unit 11). Selecting is THE moment
 * contact details get shared with that contractor, so it confirms first.
 */
function QuotesSection({ rfqId, rfqOpen }: { rfqId: string; rfqOpen: boolean }) {
  const { data } = useRfqQuotes(rfqId);
  const selectQuote = useSelectQuote(rfqId);
  const [confirming, setConfirming] = useState<string | null>(null);

  if (!data || data.quotes.length === 0) return null;
  const { quotes, selection, jobs } = data;
  // Re-selection is open when nothing is selected OR the selection was
  // voided (withdrawal — unit 11 — or contractor cancellation — unit 18).
  const selectionOpenForChoosing = !selection || selection.status === "VOIDED";

  return (
    <Section label={`Quotes (${quotes.length})`}>
      <JobTimeline jobs={jobs ?? []} selection={selection} />
      {selection?.status === "VOIDED" && (
        <p className="mb-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {selection.voidedReason === "contractor cancelled the job"
            ? "Your contractor had to cancel the job."
            : "Your selected contractor withdrew their quote."}{" "}
          Your other quotes are still open — pick another below.
        </p>
      )}
      <div className="space-y-3">
        {quotes.map((q) => {
          const isSelected = selection?.status === "SELECTED" && selection.quoteId === q.quoteId;
          const withdrawn = q.status === "WITHDRAWN";
          return (
            <div
              key={q.quoteId}
              className={`rounded-2xl border p-4 ${
                isSelected
                  ? "border-teal-300 bg-teal-50"
                  : withdrawn
                    ? "border-slate-200 bg-slate-50 opacity-60"
                    : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">
                    {q.contractorProfile?.businessName ?? "Contractor"}
                  </p>
                  {/* 0 is the "not on file" sentinel for manually-invited
                      pros — advertising "0 years experience" helps no one. */}
                  {q.contractorProfile && q.contractorProfile.yearsExperience > 0 && (
                    <p className="text-xs text-slate-500">
                      {q.contractorProfile.yearsExperience} years experience
                    </p>
                  )}
                </div>
                {isSelected && (
                  <span className="rounded-full bg-teal-700 px-2.5 py-1 text-xs font-semibold text-white">
                    Selected
                  </span>
                )}
                {withdrawn && (
                  <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">
                    Withdrawn
                  </span>
                )}
              </div>
              <p className="mt-2 text-lg font-bold text-slate-900">{formatQuotePrice(q.quote)}</p>
              {q.quote?.notes && (
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
                  {q.quote.notes}
                </p>
              )}
              {(q.quote?.availabilityWindows?.length ?? 0) > 0 && (
                <p className="mt-1 text-xs text-slate-500">
                  Available:{" "}
                  {q
                    .quote!.availabilityWindows!.map(
                      (w) => `${w.date}${w.note ? ` (${w.note})` : ""}`,
                    )
                    .join(", ")}
                </p>
              )}

              {rfqOpen && !withdrawn && selectionOpenForChoosing && (
                <div className="mt-3">
                  {confirming === q.quoteId ? (
                    <div className="space-y-2 rounded-xl bg-slate-50 p-3">
                      <p className="text-sm text-slate-700">
                        Choose this quote? Your name, address, and contact details will be shared
                        with {q.contractorProfile?.businessName ?? "this contractor"} so they can
                        reach you.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          loading={selectQuote.isPending}
                          onClick={() => selectQuote.mutate(q.quoteId)}
                        >
                          Yes, choose this pro
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setConfirming(null)}>
                          Not yet
                        </Button>
                      </div>
                      {selectQuote.error && (
                        <p className="text-sm text-rose-700">{selectQuote.error.message}</p>
                      )}
                    </div>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => setConfirming(q.quoteId)}>
                      Choose this quote
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

/**
 * Pre-bid contractor Q&A (Phase 2). Answers are shared with every invited
 * contractor, so the form says so — the customer should never assume a
 * private reply.
 */
function QuestionsList({ rfq, canAnswer }: { rfq: Rfq; canAnswer: boolean }) {
  const answerQuestion = useAnswerQuestion(rfq.id);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  return (
    <div className="space-y-3">
      {(rfq.questions ?? []).map((q) => (
        <div key={q.questionId} className="rounded-2xl border border-slate-200 bg-white p-3">
          <p className="text-[15px] font-medium leading-relaxed text-slate-900">{q.question}</p>
          {q.answer ? (
            <p className="mt-2 whitespace-pre-wrap rounded-xl bg-teal-50 p-2.5 text-sm leading-relaxed text-teal-900">
              {q.answer}
            </p>
          ) : canAnswer ? (
            <form
              className="mt-2 space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                const answer = (drafts[q.questionId] ?? "").trim();
                if (answer.length === 0) return;
                answerQuestion.mutate({ questionId: q.questionId, answer });
              }}
            >
              <textarea
                className="w-full rounded-xl border border-slate-300 p-2.5 text-[15px]"
                rows={2}
                maxLength={1000}
                placeholder="Type your answer..."
                value={drafts[q.questionId] ?? ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [q.questionId]: e.target.value }))}
              />
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-slate-500">
                  Your answer is shared with all invited contractors.
                </p>
                <Button type="submit" size="sm" disabled={answerQuestion.isPending}>
                  {answerQuestion.isPending ? "Sending..." : "Answer"}
                </Button>
              </div>
            </form>
          ) : (
            <p className="mt-2 text-sm text-slate-500">Unanswered</p>
          )}
        </div>
      ))}
    </div>
  );
}

function AttachedBinderList({ rfq }: { rfq: Rfq }) {
  return (
    <ul className="space-y-3">
      {(rfq.attachedBinder ?? []).map((e) => (
        <li key={e.entryId} className="rounded-lg border border-slate-200 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-900">{e.title}</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              {entryTypeLabels[e.entryType as BinderEntryType] ?? e.entryType}
            </span>
            {e.locationLabel && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                {e.locationLabel}
              </span>
            )}
          </div>
          {e.facts && Object.keys(e.facts).length > 0 && (
            <dl className="mt-2 space-y-0.5">
              {Object.entries(e.facts).map(([k, f]) => (
                <div key={k} className="flex justify-between gap-3 text-sm">
                  <dt className="text-slate-500">{f.label}</dt>
                  <dd className="text-slate-900">
                    {typeof f.value === "boolean" ? (f.value ? "Yes" : "No") : String(f.value)}
                    {f.unit ? ` ${f.unit}` : ""}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {e.notes && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{e.notes}</p>}
          {e.media.length > 0 && (
            <ul className="mt-2 grid grid-cols-4 gap-2">
              {e.media.map((m) => (
                <li key={m.mediaId}>
                  <BinderMediaThumb
                    propertyId={rfq.propertyId}
                    entryId={e.entryId}
                    mediaId={m.mediaId}
                  />
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-2.5">{children}</div>
    </section>
  );
}

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-md">
      <div className="border-b border-slate-200 bg-white px-4 pb-4 pt-4">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="mt-3 h-7 w-2/3" />
        <Skeleton className="mt-2 h-3 w-1/3" />
      </div>
      <div className="space-y-7 px-4 py-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    </div>
  );
}

function ImageGallery({
  rfqId,
  images,
  canEdit,
}: {
  rfqId: string;
  images: RfqImage[];
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const deleteMutation = useDeleteRfqImage(rfqId);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lightboxId, setLightboxId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  async function onAdd(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadError(null);
    setUploading(true);
    // Collect rejections and flush once: setting the error inside the loop made
    // the last rejection win and left a stale message after a later success.
    const rejected: string[] = [];
    try {
      for (const file of Array.from(files)) {
        const contentType = detectUploadMime(file);
        if (!contentType) {
          rejected.push(`${file.name}: unsupported image type`);
          continue;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
          rejected.push(`${file.name} is too large (max 25 MB)`);
          continue;
        }
        await uploadRfqImage({ rfqId, file, contentType });
      }
      // Refetch the RFQ so the gallery picks up the freshly attached images.
      await qc.invalidateQueries({ queryKey: ["rfqs", rfqId] });
      setUploadError(rejected.length > 0 ? rejected.join("; ") : null);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  if (images.length === 0 && !canEdit) {
    return <p className="text-sm text-slate-500">No photos added.</p>;
  }

  // Per design.md §8: "remove the X overlay unless deleting" — we toggle an
  // explicit edit mode instead of always-on delete buttons. Keeps the
  // gallery clean for the common "just look at the photos" case.
  const showDelete = canEdit && editMode;

  return (
    <div className="space-y-3">
      {images.length > 0 && (
        <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {images.map((img) => (
            <li key={img.id}>
              <ImageTile
                rfqId={rfqId}
                image={img}
                showDelete={showDelete}
                onOpen={() => setLightboxId(img.id)}
                onDelete={() => deleteMutation.mutate(img.id)}
                isDeleting={deleteMutation.isPending && deleteMutation.variables === img.id}
              />
            </li>
          ))}
        </ul>
      )}

      {uploadError && <p className="text-xs text-rose-700">{uploadError}</p>}

      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <label className="press inline-flex min-h-[44px] cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 text-sm font-semibold text-brand-700 hover:bg-brand-50">
            <CameraIcon className="h-5 w-5" />
            {uploading ? "Uploading…" : images.length === 0 ? "Add photo" : "Add more"}
            <input
              type="file"
              accept="image/*"
              multiple
              disabled={uploading}
              className="sr-only"
              onChange={(e) => {
                void onAdd(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
          {images.length > 0 && (
            <button
              type="button"
              onClick={() => setEditMode((v) => !v)}
              className="press min-h-[44px] rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {editMode ? "Done" : "Edit photos"}
            </button>
          )}
        </div>
      )}

      {lightboxId && (
        <Lightbox rfqId={rfqId} imageId={lightboxId} onClose={() => setLightboxId(null)} />
      )}
    </div>
  );
}

function ImageTile({
  rfqId,
  image,
  showDelete,
  onOpen,
  onDelete,
  isDeleting,
}: {
  rfqId: string;
  image: RfqImage;
  showDelete: boolean;
  onOpen: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const { data, isLoading, error } = useRfqImageUrl(rfqId, image.id);
  return (
    <div className="relative">
      {isLoading || !data ? (
        <div className="skeleton aspect-square w-full rounded-xl" />
      ) : error ? (
        <div className="flex aspect-square w-full items-center justify-center rounded-xl bg-slate-100 text-xs text-slate-500">
          Unavailable
        </div>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          className="press block w-full overflow-hidden rounded-xl border border-slate-200"
          aria-label="Open photo"
        >
          <img src={data.url} alt="" className="aspect-square w-full object-cover" />
        </button>
      )}
      {showDelete && (
        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          aria-label="Delete photo"
          className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-rose-600 text-white shadow-md hover:bg-rose-700 disabled:opacity-50"
        >
          {isDeleting ? "…" : <XMarkIcon className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}

function Lightbox({
  rfqId,
  imageId,
  onClose,
}: {
  rfqId: string;
  imageId: string;
  onClose: () => void;
}) {
  const { data } = useRfqImageUrl(rfqId, imageId);
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/90 p-2"
    >
      {data ? (
        <img
          src={data.url}
          alt=""
          // Stop the click from bubbling so tapping the photo itself doesn't
          // close the lightbox; the surrounding backdrop still does.
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <div className="text-sm text-white">Loading…</div>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-3 top-[max(env(safe-area-inset-top),0.75rem)] inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
}

/**
 * Vertical timeline showing the request's progress. See design.md §8 — this
 * is intentionally a connected timeline (not a bullet list) so the user
 * reads it as motion through stages.
 */
function StatusTimeline({
  rfq,
}: {
  rfq: {
    status: "SUBMITTED" | "IN_REVIEW" | "CLOSED" | "CANCELLED";
    createdAt: string;
    updatedAt: string;
  };
}) {
  type Tone = "done" | "current" | "pending" | "cancelled";
  const steps: { label: string; at: string | null; tone: Tone }[] = [];
  steps.push({ label: "Submitted", at: rfq.createdAt, tone: "done" });
  if (rfq.status === "CANCELLED") {
    steps.push({ label: "Cancelled", at: rfq.updatedAt, tone: "cancelled" });
  } else if (rfq.status === "IN_REVIEW") {
    steps.push({ label: "In review", at: rfq.updatedAt, tone: "current" });
    steps.push({ label: "Closed", at: null, tone: "pending" });
  } else if (rfq.status === "CLOSED") {
    steps.push({ label: "In review", at: null, tone: "done" });
    steps.push({ label: "Closed", at: rfq.updatedAt, tone: "done" });
  } else {
    steps.push({ label: "In review", at: null, tone: "pending" });
    steps.push({ label: "Closed", at: null, tone: "pending" });
  }

  return (
    <ol className="relative ml-2 border-l-2 border-slate-200">
      {steps.map((s, i) => (
        <li key={i} className="relative pb-5 pl-5 last:pb-0">
          <span
            aria-hidden
            className={[
              "absolute -left-[11px] top-0 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-slate-50",
              dotBg(s.tone),
            ].join(" ")}
          >
            {s.tone === "done" ? (
              <CheckIcon className="h-3 w-3 text-white" />
            ) : s.tone === "current" ? (
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
            ) : s.tone === "cancelled" ? (
              <XMarkIcon className="h-3 w-3 text-white" />
            ) : null}
          </span>
          <p
            className={[
              "text-sm font-medium",
              s.tone === "pending" ? "text-slate-500" : "text-slate-900",
            ].join(" ")}
          >
            {s.label}
          </p>
          {s.at && (
            <p className="mt-0.5 text-xs text-slate-500">
              {new Date(s.at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}

/**
 * Post-selection job progress (Phase 2 unit 18), projected from
 * contractor.job.status-changed events. Same visual language as
 * StatusTimeline. Shows the job for the active selection; after a
 * cancellation + re-selection the newest job wins.
 */
function JobTimeline({
  jobs,
  selection,
}: {
  jobs: JobProjection[];
  selection: QuoteSelection | null;
}) {
  if (jobs.length === 0) return null;
  const active =
    (selection && jobs.find((j) => j.quoteId === selection.quoteId)) ??
    [...jobs].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]!;

  type Tone = "done" | "current" | "pending" | "cancelled";
  const chain = ["SELECTED", "ACCEPTED", "SCHEDULED", "IN_PROGRESS", "COMPLETED"] as const;
  const labels: Record<(typeof chain)[number], string> = {
    SELECTED: "Contractor selected",
    ACCEPTED: "Accepted",
    SCHEDULED: active.scheduledFor ? `Scheduled for ${active.scheduledFor}` : "Scheduled",
    IN_PROGRESS: "Work in progress",
    COMPLETED: "Completed",
  };
  const cancelled = active.status === "CANCELLED";

  const steps: { label: string; at: string | null; tone: Tone }[] = [];
  for (const s of chain) {
    const at = active.statusTimestamps[s];
    if (!at) continue;
    steps.push({
      label: labels[s],
      at,
      tone: active.status === s && s !== "COMPLETED" ? "current" : "done",
    });
  }
  if (cancelled) {
    steps.push({
      label: "Cancelled by the contractor",
      at: active.statusTimestamps.CANCELLED ?? null,
      tone: "cancelled",
    });
  } else if (active.status !== "COMPLETED") {
    steps.push({ label: "Completed", at: null, tone: "pending" });
  }

  return (
    <div className="mb-3 rounded-2xl border border-slate-200 bg-white p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Job status
      </p>
      <ol className="relative ml-2 border-l-2 border-slate-200">
        {steps.map((s, i) => (
          <li key={i} className="relative pb-5 pl-5 last:pb-0">
            <span
              aria-hidden
              className={[
                "absolute -left-[11px] top-0 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-white",
                dotBg(s.tone),
              ].join(" ")}
            >
              {s.tone === "done" ? (
                <CheckIcon className="h-3 w-3 text-white" />
              ) : s.tone === "current" ? (
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
              ) : s.tone === "cancelled" ? (
                <XMarkIcon className="h-3 w-3 text-white" />
              ) : null}
            </span>
            <p
              className={[
                "text-sm font-medium",
                s.tone === "pending" ? "text-slate-500" : "text-slate-900",
              ].join(" ")}
            >
              {s.label}
            </p>
            {s.at && (
              <p className="mt-0.5 text-xs text-slate-500">
                {new Date(s.at).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function dotBg(tone: "done" | "current" | "pending" | "cancelled"): string {
  switch (tone) {
    case "done":
      return "bg-brand-700";
    case "current":
      return "bg-amber-500";
    case "cancelled":
      return "bg-rose-600";
    case "pending":
      return "border-2 border-slate-300 bg-white";
  }
}
